/**
 * Chain-anchor facts a CLIENT may import (B3 leaf).
 *
 * This is not a client-side shim of `chain-anchor.ts` — it is the sole
 * definition of everything here, and `chain-anchor.ts` re-exports from it. A
 * leaf that re-declared these would reintroduce, one layer down, the exact
 * duplication it exists to remove.
 *
 * WHY IT IS A SEPARATE MODULE. `chain-anchor.ts` imports `verificationSha256`
 * from `evidence-lineage.ts`, which imports `node:crypto`. The shell's
 * browser-only guard correctly refuses `node:crypto` in the client bundle, and
 * it fires during transform — so importing the anchoring module for two display
 * helpers took down every route in the prototype, not just the page that asked
 * for it. The hash is needed by exactly one function (`buildChainAnchor`), and
 * nothing in this file needs it.
 *
 * THE CONSTRAINT THIS FILE IS UNDER: it must import nothing that reaches a node
 * builtin, transitively. That is a property of the whole import graph, not of
 * this file's text, and it is asserted that way — a gate that only grepped this
 * file would go green again the moment someone added a single innocuous-looking
 * import to a module that has one nine hops down. Which is how the original
 * failure happened.
 */
export const CHAIN_ANCHOR_TARGETS = [
    "customer-worm-bucket",
    "rfc3161-tsa",
    "operator-held-file",
];
/**
 * Whether a rollback attack is detectable under the given inputs. Exported so
 * a caller — or a UI — can state the limitation instead of discovering it.
 *
 * A hash chain proves its own internal consistency and nothing else, and an
 * anchor cannot establish that it is the CURRENT anchor. A genuine anchor from
 * an earlier honest state, presented with the ledger rewound to match it, is
 * internally flawless. Only a pin the reader holds independently distinguishes
 * it. Where there is no pin this says so plainly rather than implying a
 * protection that is not there.
 */
export function chainAnchorRollbackCoverage(expectation) {
    if (expectation == null) {
        return {
            detectable: false,
            statement: "No independently held anchor reference was supplied, so this check cannot detect a rollback: a genuine anchor from an earlier state, presented with the ledger rewound to match it, would verify exactly like a current one.",
        };
    }
    return {
        detectable: true,
        statement: `Rollback is detectable because the reader supplied the expected anchor ${expectation.expectedAnchorId} independently of the pack.`,
    };
}
